// Sales dashboard companions (other Business UI handoff, W01):
//   • "This week, in hand." — projects whose in-hands date is in the next 7
//     days (or already passed and still open), soonest first.
//   • "Work in motion." — the rep's open projects with stage, in-hands date
//     and last activity; each row opens the project.
// Both read the existing projects list (GET /projects?client_rep_id=me) — no
// new figures are invented here.
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { projectStageLabel } from "@/lib/projectStage";
import { timeAgo } from "@/lib/utils";

interface ProjectRow {
  id: string;
  projectNumber: number;
  clientName: string;
  name: string;
  statusStage: string;
  statusState: string;
  inHandsDate: string;
  updatedAt: string;
}

const CLOSED = new Set(["closed", "cancelled", "lost"]);
const DAY = 86_400_000;

function useMyOpenProjects() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["projects", "mine-open", user?.sub],
    enabled: !!user?.sub,
    queryFn: async () => {
      const r = await api<{ rows: ProjectRow[] }>(`/projects?client_rep_id=${user!.sub}&limit=100`);
      return r.rows.filter((p) => !CLOSED.has(p.statusStage) && !CLOSED.has(p.statusState));
    },
  });
}

const dayLabel = (iso: string) =>
  new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function ThisWeekInHand() {
  const { data, isLoading, isError } = useMyOpenProjects();
  const now = Date.now();
  const soon = (data ?? [])
    .filter((p) => p.inHandsDate && new Date(`${p.inHandsDate.slice(0, 10)}T12:00:00`).getTime() <= now + 7 * DAY)
    .sort((a, b) => a.inHandsDate.localeCompare(b.inHandsDate))
    .slice(0, 5);
  return (
    <section className="rounded-md border border-rule bg-white p-[22px]">
      <h2 className="mb-3.5 text-lg font-semibold tracking-[-0.4px] text-ink">This week, in hand.</h2>
      {isLoading ? (
        <div className="space-y-3" aria-busy="true">
          {[0, 1, 2].map((i) => <div key={i} className="h-9 animate-pulse rounded bg-canvas" />)}
        </div>
      ) : isError ? (
        <p role="alert" className="text-sm text-risk">We couldn't load your in-hands dates.</p>
      ) : soon.length === 0 ? (
        <p className="text-sm text-muted">Nothing due in the next seven days.</p>
      ) : (
        <ul>
          {soon.map((p) => {
            const overdue = new Date(`${p.inHandsDate.slice(0, 10)}T23:59:59`).getTime() < now;
            return (
              <li key={p.id} className="border-b border-[#eee] last:border-0">
                <Link to={`/projects/${p.id}`} className="-mx-2 block rounded px-2 py-3 hover:bg-[#f1f1ee]">
                  <span className="block font-semibold text-ink">{dayLabel(p.inHandsDate)} · {p.clientName}</span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {p.name} · {projectStageLabel(p.statusStage)}
                    {overdue ? <span className="ml-2 rounded bg-risk-bg px-1.5 py-0.5 text-[11px] text-risk">Past in-hands date</span> : null}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function WorkInMotion({ className = "mt-6" }: { className?: string } = {}) {
  // The whole row opens the project, not just its name (Stephanie 2026-10-12:
  // "why aren't these projects clickable?"). The name stays a real link for
  // keyboard / screen-reader users and for opening with a modifier key.
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMyOpenProjects();
  // The 5 most recently touched (Stephanie 2026-10-13: the dashboard "looks
  // too busy"). Due dates already have "This week, in hand"; the full list
  // lives on Projects.
  const all = data ?? [];
  const rows = [...all].sort((a, b) => (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "")).slice(0, 5);
  return (
    <section className={className}>
      <div className="mb-3.5 flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">Work in motion.</h2>
        <Link to="/projects" className="inline-flex min-h-[40px] items-center rounded border border-rule bg-white px-3.5 text-sm text-ink hover:bg-canvas">
          {all.length > rows.length ? `View all ${all.length} projects` : "View projects"}
        </Link>
      </div>
      <div className="overflow-x-auto rounded-md border border-rule bg-white">
        <table className="w-full whitespace-nowrap text-left">
          <thead className="border-b border-[#e7e7e4] bg-[#fafaf8] text-[11px] font-medium text-muted">
            <tr>
              <th scope="col" className="px-[18px] py-[15px] font-medium">Project / client</th>
              <th scope="col" className="px-[18px] py-[15px] font-medium">Stage</th>
              <th scope="col" className="px-[18px] py-[15px] font-medium">In hands</th>
              <th scope="col" className="px-[18px] py-[15px] font-medium">Last update</th>
            </tr>
          </thead>
          <tbody className="text-[13px]">
            {isLoading ? (
              <tr><td colSpan={4} className="px-[18px] py-[15px] text-muted">Loading your projects…</td></tr>
            ) : isError ? (
              <tr><td colSpan={4} className="px-[18px] py-[15px] text-risk" role="alert">We couldn't load your projects.</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={4} className="px-[18px] py-[15px] text-muted">No open projects yet. Start one with New project.</td></tr>
            ) : (
              rows.map((p) => (
                <tr
                  key={p.id}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest("a")) return;
                    navigate(`/projects/${p.id}`);
                  }}
                  className="cursor-pointer border-b border-[#e7e7e4] last:border-0 hover:bg-[#f1f1ee]"
                >
                  <td className="px-[18px] py-[15px]">
                    <Link to={`/projects/${p.id}`} className="font-medium text-ink hover:underline">{p.name}</Link>
                    <span className="block text-[11px] text-muted">{p.clientName} · #{p.projectNumber}</span>
                  </td>
                  <td className="px-[18px] py-[15px]">
                    <span className="inline-block rounded bg-[#f1f1ee] px-2 py-1 text-[11px]">{projectStageLabel(p.statusStage)}</span>
                  </td>
                  <td className="px-[18px] py-[15px] tabular-nums">{p.inHandsDate ? dayLabel(p.inHandsDate) : "—"}</td>
                  <td className="px-[18px] py-[15px] text-muted">{timeAgo(p.updatedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
